import { AI_SERVICES_ORIGIN } from "../config/apiUrls";
import { fetchImage } from "./imageReadService";

/**
 * Background Removal Service
 *
 * Uses the configured remove-bg API (e.g. https://google-ai.mosida.com/remove-bg).
 * When the API returns `image_url` / `url`, that absolute URL is returned as-is so
 * downstream APIs (e.g. generate-character) receive a fetchable URL. Falls back to
 * a data URL when the API returns only raw image bytes or base64.
 */

export interface FreepikBgRemovalResponse {
  success: boolean;
  blobUrl?: string;
  error?: string;
}

/**
 * Convert File, Blob, or data/blob URL string into a File for upload.
 */
async function toFile(input: File | Blob | string): Promise<File> {
  if (input instanceof File) return input;
  if (input instanceof Blob) return new File([input], "image.png", { type: "image/png" });
  const response = await fetchImage(input);
  const blob = await response.blob();
  return new File([blob], "image.png", { type: "image/png" });
}

/** Convert a Blob to a data URL (base64) so it works across tabs/devices. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Normalize base64 to a full data URL. */
function toDataUrl(base64: string): string {
  return base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
}

/** Turn API `image_url` / `url` into an absolute URL the generate-character backend can fetch. */
function toAbsoluteImageUrl(imageUrl: string, apiBase: string): string {
  const t = imageUrl.trim();
  if (t.startsWith("data:")) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  const origin = apiBase.replace(/\/$/, "");
  if (t.startsWith("/")) return `${origin}${t}`;
  return `${origin}/${t}`;
}

/**
 * Call remove-bg API: POST image, handle JSON (image_url / base64) or direct image body.
 */
async function removeBackgroundWithRemoveBgApi(file: File): Promise<string> {
  const baseUrl = AI_SERVICES_ORIGIN.replace(/\/$/, "");

  const formData = new FormData();
  formData.append("image", file);

  const headers: Record<string, string> = {};
  const apiKey = process.env.REACT_APP_REMOVE_BG_API_KEY;
  if (apiKey) {
    headers["X-API-Key"] = apiKey;
  }

  const res = await fetch(`${baseUrl}/remove-bg`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Background removal failed: ${res.status} ${text}`);
  }

  const contentType = res.headers.get("content-type") || "";

  // Response is direct image (e.g. image/png)
  if (contentType.indexOf("image/") === 0) {
    const blob = await res.blob();
    return blobToDataUrl(blob);
  }

  // Response is JSON
  const data = await res.json();
  if (data && typeof data.success === "boolean" && !data.success) {
    throw new Error(data.message || "Background removal failed");
  }

  // JSON: image_url or url — pass through for generate-character / other URL-based APIs
  const imageUrl = data?.image_url || data?.url;
  if (imageUrl && typeof imageUrl === "string") {
    return toAbsoluteImageUrl(imageUrl, baseUrl);
  }

  // JSON: base64 image (common field names)
  const base64 =
    data?.image_base64 ??
    data?.image_url_base64 ??
    data?.img_without_background_base64 ??
    data?.base64 ??
    data?.image;
  if (base64 && typeof base64 === "string") {
    return toDataUrl(base64);
  }

  throw new Error("API did not return image_url, url, or base64 image");
}

/**
 * Remove background from image using the configured remove-bg API.
 *
 * @param file - File, Blob, or data URL string to process
 * @returns Public `image_url`/`url` from the API when present; otherwise a data URL (binary or base64 responses).
 */
export async function removeBackgroundWithFreepik(
  file: File | Blob | string
): Promise<string> {
  try {
    const fileToProcess = await toFile(file);
    return await removeBackgroundWithRemoveBgApi(fileToProcess);
  } catch (error: any) {
    throw new Error(error.message || "Background removal failed");
  }
}
